<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { useOrderedMacros } from 'dashboard/composables/useOrderedMacros';
import { useMacros } from 'dashboard/composables/useMacros';
import {
  applyRecentGroups,
  useRecentPickerIds,
} from 'dashboard/composables/useRecentPickerIds';
import CaretAnchoredPicker from 'dashboard/components-next/preview-picker/CaretAnchoredPicker.vue';

const props = defineProps({
  caretPosition: {
    type: Object,
    default: null,
  },
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectMacro', 'close', 'removeTrigger']);

const store = useStore();
const { t } = useI18n();

const { orderedMacros } = useOrderedMacros();
const { resolveMacroActions } = useMacros();

const uiFlags = useMapGetter('macros/getUIFlags');
const { recentIds, remember } = useRecentPickerIds(
  LOCAL_STORAGE_KEYS.RECENT_MACROS
);

// The trigger can already be followed by text, from a draft or a caret moved back onto it
const searchQuery = ref(props.searchKey);

const searchTerm = computed(() => searchQuery.value.trim().toLowerCase());

const items = computed(() => {
  const mapped = orderedMacros.value
    .filter(macro => macro.name?.toLowerCase().includes(searchTerm.value))
    .map(macro => ({
      id: macro.id,
      macro,
      label: `#${macro.name}`,
      title: `#${macro.name}`,
    }));

  if (searchTerm.value) return mapped;

  return applyRecentGroups(mapped, recentIds.value, {
    recentLabel: t('CONVERSATION.PICKER.RECENT'),
    allLabel: t('CONVERSATION.PICKER.ALL'),
  });
});

const onSelect = item => {
  remember(item.id);
  emit('selectMacro', item.macro);
};

onMounted(() => {
  if (!orderedMacros.value.length) store.dispatch('macros/get');
});
</script>

<template>
  <CaretAnchoredPicker
    v-model:search="searchQuery"
    :caret-position="caretPosition"
    :items="items"
    :search-placeholder="t('CONVERSATION.PICKER.MACRO.SEARCH_PLACEHOLDER')"
    :hint="t('CONVERSATION.PICKER.HINT')"
    :is-loading="uiFlags.isFetching"
    :empty-label="t('COMBOBOX.EMPTY_STATE')"
    @select="onSelect"
    @close="emit('close')"
    @remove-trigger="emit('removeTrigger')"
  >
    <template #preview="{ item }">
      <div v-if="item" class="px-5 py-4">
        <div
          v-for="(action, index) in resolveMacroActions(item.macro)"
          :key="index"
          class="relative flex flex-col pb-2 group ps-4 last:pb-0 gap-0.5"
        >
          <span
            class="absolute top-1 -bottom-1 w-px start-[3.5px] bg-n-slate-6 group-last:hidden"
          />
          <span
            class="absolute border-2 rounded-full top-1 start-0 size-2 bg-n-solid-1 border-n-weak dark:border-n-slate-6"
          />
          <span class="text-xs text-n-slate-10">
            {{ t(`MACROS.ACTIONS.${action.actionName}`) }}
          </span>
          <span
            v-if="action.actionValue"
            class="text-sm break-words text-n-slate-12"
          >
            {{ action.actionValue }}
          </span>
        </div>
      </div>
    </template>
  </CaretAnchoredPicker>
</template>
